import { useCallback, useEffect, useState } from 'react';
import type {
  MemoryListResponse,
  MemoryRow,
} from '@verybigthings/trackframe-shared/schemas';
import { hub } from '@/lib/hub-client';
import { UserMemoryView, type ScopeFilter } from './memory-view';

// Per-user Memory tab on the user-detail page (MEM-10, COP-032, D-20).
// Lists the TARGET URL user's tf_user_memory rows by calling the admin-only
// GET /users/:id/memory endpoint via the typed `hub.adminListMemory` SDK
// method (declared in packages/hub-client/src/endpoints.ts). The route is
// gated by `requireAdmin` and wraps the data read in `withUserRole(targetUserId)`
// server-side so RLS still validates the target user's role (D-09, HUB-024 §6).
//
// Data fetching + `hub.*` calls live here; the props-only `UserMemoryView`
// (./memory-view) owns the presentational markup so it stays renderable under
// the admin's no-jsdom test convention (M12 list-view precedent).

interface UserMemoryProps {
  /** TARGET user id, from the URL params on the user-detail page. */
  userId: string;
  /**
   * Admin's own user id; used to conditionally enable the delete buttons.
   * See D-20 — the DELETE /memory/:id[/permanent] routes are keyed off
   * `request.user!.id`, so admin-initiated deletes of OTHER users' rows are
   * deferred. When adminUserId === userId, deletes proceed normally.
   */
  adminUserId?: string;
}

export function UserMemory({ userId, adminUserId }: UserMemoryProps) {
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [rows, setRows] = useState<MemoryRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<MemoryRow | null>(null);
  const [permanentDeleteBusy, setPermanentDeleteBusy] = useState(false);

  // Delete uses routes keyed off request.user!.id — so they only work when the
  // admin IS the target user. Disable otherwise (D-20).
  const canDelete = adminUserId !== undefined && adminUserId === userId;

  // Admin-only listing — hits GET /users/:id/memory (plan-8 TASK-COP-23) via
  // the typed SDK method. The route requires system#admin (gated by
  // requireAdmin in users.ts) and wraps the data read in
  // withUserRole(targetUserId) so RLS still validates the target user's role
  // server-side (HUB-024 §6, D-09).
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const query: { scope?: 'global' | 'track' } =
        scopeFilter === 'all' ? {} : { scope: scopeFilter };
      const res: MemoryListResponse = await hub.adminListMemory({
        params: { id: userId },
        query,
      });
      setRows(res.memories);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load memory');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, scopeFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!canDelete) return;
      setPendingDeleteId(id);
      try {
        await hub.memoryDelete({ params: { id } });
        await refresh();
      } catch (err) {
        setError((err as Error).message ?? 'Failed to soft-delete memory');
      } finally {
        setPendingDeleteId(null);
      }
    },
    [canDelete, refresh],
  );

  // IMEM-20 / COP-105 permanent wipe — confirm-gated, then hits the deliberate
  // DELETE /memory/:id/permanent route via the typed SDK method. Chat forget
  // never reaches this; this is the only outside caller of the hard-delete.
  const handleConfirmPermanentDelete = useCallback(async () => {
    if (!canDelete || !permanentDeleteTarget) return;
    setPermanentDeleteBusy(true);
    try {
      await hub.memoryDeletePermanent({ params: { id: permanentDeleteTarget.id } });
      setPermanentDeleteTarget(null);
      await refresh();
    } catch (err) {
      setError((err as Error).message ?? 'Failed to permanently delete memory');
    } finally {
      setPermanentDeleteBusy(false);
    }
  }, [canDelete, permanentDeleteTarget, refresh]);

  return (
    <UserMemoryView
      scopeFilter={scopeFilter}
      setScopeFilter={setScopeFilter}
      rows={rows}
      isLoading={isLoading}
      error={error}
      canDelete={canDelete}
      pendingSoftDeleteId={pendingDeleteId}
      onSoftDelete={handleDelete}
      refresh={refresh}
      permanentDeleteTarget={permanentDeleteTarget}
      permanentDeleteBusy={permanentDeleteBusy}
      onRequestPermanentDelete={setPermanentDeleteTarget}
      onConfirmPermanentDelete={handleConfirmPermanentDelete}
      onCancelPermanentDelete={() => setPermanentDeleteTarget(null)}
    />
  );
}
